/**
 * The completeness check: a custom printout may print only when every shift's name is visibly on the page.
 *
 * Every function here is SELF-CONTAINED — no imports, no module-level references — because the harness
 * embeds each one into the sandboxed iframe with `String(fn)`; `checkCompleteness` gets its parts as the
 * `checks` argument for the same reason. Keep every constant and helper inside its function's body.
 * Geometry comes from an injected `CompletenessMeasure`, so tests can stub layout (jsdom has none).
 *
 * What it looks at is the NAME TEXT, not just the element tagged with the shift's id: an initials cell with
 * the full name in a hidden span, or a name cut off by its cell, fails even though the tagged element shows.
 *
 * It guards against a template's mistakes, not a hostile author.
 */

/** A box in viewport coordinates. Every box a measure returns uses the same coordinates. */
export type CompletenessBox = { left: number; top: number; width: number; height: number };

/** `overflowX`/`overflowY` read "visible" for a box that can't clip (inline, `display: contents`, table rows…). */
export type CompletenessStyle = { overflowX: string; overflowY: string; fontSizePx: number; colorAlpha: number };

/** Characters [start, end) of one text node. */
export type CompletenessTextRun = { node: Text; start: number; end: number };

export type CompletenessMeasure = {
	/** `display:none`, `visibility:hidden`, `opacity:0`, `content-visibility:hidden` (self or ancestor) and closed `<details>` are not visible. */
	isVisible: (element: Element) => boolean;
	styleOf: (element: Element) => CompletenessStyle;
	/** Where a text run is drawn: one box per line fragment. Empty when it isn't laid out. */
	textBoxes: (run: CompletenessTextRun) => CompletenessBox[];
	/** The box an element clips overflowing content to (its padding box). */
	clipBoxOf: (element: Element) => CompletenessBox;
	/** Whether the element's content is bigger than its clip box: `scrollWidth > clientWidth` (x), `scrollHeight > clientHeight` (y). */
	overflowOf: (element: Element) => { x: boolean; y: boolean };
	/** The paper: the root element's box, which the harness lays out at the printable width. */
	pageBox: () => CompletenessBox;
};

export type CompletenessShift = { id: string; name: string };

export type CompletenessReport = {
	ok: boolean;
	/** Shift ids whose name no tagged element shows in full, readably. */
	missing: string[];
	/** `data-shift-id` values that match no shift. A warning only; never blocks printing. */
	unknownIds: string[];
	/**
	 * A stylesheet has an `@media` or `@container` rule, or a media list other than "all" (`<style media>`), so
	 * the page could look different on paper or at the paper's width than in the screen render the check ran on.
	 * Blocks printing.
	 */
	mediaRules: boolean;
};

/**
 * Where `name` occurs in the text inside `element`: for each occurrence, the text runs that draw it. Matching
 * ignores case and collapses whitespace, so "Jane  Doe" across `<b>Jane</b> <i>Doe</i>` is found. Runs that
 * are only whitespace are left out — a collapsed space draws nothing to check.
 */
export function findNameRuns(element: Element, name: string): CompletenessTextRun[][] {
	const SHOW_TEXT = 4; // NodeFilter.SHOW_TEXT, written out so the function needs no global
	const WHITESPACE = /\s/;
	type Origin = { node: Text | null; offset: number; length: number; isSpace: boolean };

	/** Appends `text` folded (lower case, one space per whitespace run) with where each character came from. */
	const fold = (text: string, node: Text | null, chars: string[], origins: Origin[]): void => {
		let offset = 0;
		for (const character of text) {
			if (!WHITESPACE.test(character)) {
				// Per UTF-16 unit, so `chars` indexes line up with the joined string's.
				for (const unit of character.toLocaleLowerCase().split("")) {
					chars.push(unit);
					origins.push({ node, offset, length: character.length, isSpace: false });
				}
			} else if (chars.length > 0 && chars[chars.length - 1] !== " ") {
				chars.push(" ");
				origins.push({ node, offset, length: character.length, isSpace: true });
			}
			offset += character.length;
		}
	};
	/** One run per text node an occurrence touches, spanning the characters it uses there. */
	const runsOf = (origins: Origin[]): CompletenessTextRun[] => {
		const runs = new Map<Text, CompletenessTextRun & { drawn: boolean }>();
		for (const { node, offset, length, isSpace } of origins) {
			if (node === null) continue;
			const run = runs.get(node);
			if (run === undefined) runs.set(node, { node, start: offset, end: offset + length, drawn: !isSpace });
			else Object.assign(run, { end: offset + length, drawn: run.drawn || !isSpace });
		}
		return [...runs.values()].filter((run) => run.drawn).map(({ node, start, end }) => ({ node, start, end }));
	};

	const needleChars: string[] = [];
	fold(name, null, needleChars, []);
	const needle = needleChars.join("").trimEnd();
	const chars: string[] = [];
	const origins: Origin[] = [];
	const walker = element.ownerDocument.createTreeWalker(element, SHOW_TEXT);
	for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
		const text = node as Text; // SHOW_TEXT yields only Text nodes
		fold(text.data, text, chars, origins);
	}
	const haystack = chars.join("");
	const occurrences: CompletenessTextRun[][] = [];
	if (needle.length === 0) return occurrences;
	for (let index = haystack.indexOf(needle); index >= 0; index = haystack.indexOf(needle, index + 1)) {
		occurrences.push(runsOf(origins.slice(index, index + needle.length)));
	}
	return occurrences;
}

/**
 * Whether every run of one occurrence of a name is shown: visible, readable, on the paper and not clipped
 * by any box between it and the root — the tagged element and its descendants included.
 *
 * Inside the tagged element a clipping box whose content overflows counts as cutting the name off, even
 * where its geometry still looks inside: that is how `text-overflow: ellipsis` truncates. Above the tagged
 * element, overflow may be someone else's, so only the name's own boxes are compared with the clip box.
 */
export function isNameShown(tagged: Element, runs: CompletenessTextRun[], measure: CompletenessMeasure): boolean {
	const MIN_FONT_PX = 6;
	// Text drawn shorter than this has been scaled away (a transform), whatever its font size says.
	const MIN_GLYPH_HEIGHT_PX = 4;
	const EDGE_TOLERANCE_PX = 1;
	const VISIBLE = "visible";

	const withinX = (box: CompletenessBox, outer: CompletenessBox): boolean =>
		box.left >= outer.left - EDGE_TOLERANCE_PX && box.left + box.width <= outer.left + outer.width + EDGE_TOLERANCE_PX;
	const withinY = (box: CompletenessBox, outer: CompletenessBox): boolean =>
		box.top >= outer.top - EDGE_TOLERANCE_PX && box.top + box.height <= outer.top + outer.height + EDGE_TOLERANCE_PX;
	// The paper runs on down for as many pages as it takes; only its sides and its top cut anything off.
	const isOnPaper = (box: CompletenessBox, page: CompletenessBox): boolean => withinX(box, page) && box.top >= page.top - EDGE_TOLERANCE_PX;
	const clips = (ancestor: Element, boxes: CompletenessBox[]): boolean => {
		const style = measure.styleOf(ancestor);
		const clipsX = style.overflowX !== VISIBLE;
		const clipsY = style.overflowY !== VISIBLE;
		if (!clipsX && !clipsY) return false;
		if (tagged.contains(ancestor)) {
			const overflow = measure.overflowOf(ancestor);
			if ((clipsX && overflow.x) || (clipsY && overflow.y)) return true;
		}
		const clipBox = measure.clipBoxOf(ancestor);
		return boxes.some((box) => (clipsX && !withinX(box, clipBox)) || (clipsY && !withinY(box, clipBox)));
	};
	const isRunShown = (run: CompletenessTextRun): boolean => {
		const parent = run.node.parentElement;
		if (parent === null || !measure.isVisible(parent)) return false;
		const style = measure.styleOf(parent);
		if (style.fontSizePx < MIN_FONT_PX || style.colorAlpha <= 0) return false;
		const boxes = measure.textBoxes(run).filter((box) => box.width > 0 && box.height > 0);
		if (boxes.length === 0 || boxes.some((box) => box.height < MIN_GLYPH_HEIGHT_PX)) return false;
		const page = measure.pageBox();
		if (!boxes.every((box) => isOnPaper(box, page))) return false;
		const root = run.node.ownerDocument.documentElement;
		for (let ancestor: Element | null = parent; ancestor !== null && ancestor !== root; ancestor = ancestor.parentElement) {
			if (clips(ancestor, boxes)) return false;
		}
		return true;
	};
	return runs.length > 0 && runs.every(isRunShown);
}

/**
 * Whether any stylesheet styles by medium or box size: an `@media` or `@container` rule anywhere (nested in
 * `@supports`, `@layer` or a style rule included), an `@import` or a sheet whose media list isn't "all".
 * Any of them can make paper differ from the screen render the check measured.
 */
export function hasMediaRules(doc: Document): boolean {
	const isAllMedia = (mediaText: string): boolean => ["", "all"].includes(mediaText.trim().toLowerCase());
	// Duck-typed, so it works in any realm and any browser: @import carries `href` and `media`, @media carries
	// `media`, @container carries `containerName`, grouping and nesting rules carry `cssRules`.
	const dependsOnMedia = (rules: CSSRuleList): boolean =>
		Array.from(rules).some((rule) => {
			// Each cast below is guarded by the `in` check before it.
			if ("href" in rule) return !isAllMedia((rule as CSSImportRule).media.mediaText);
			if ("media" in rule || "containerName" in rule) return true;
			return "cssRules" in rule && dependsOnMedia((rule as CSSGroupingRule).cssRules);
		});
	return Array.from(doc.styleSheets).some((sheet) => !isAllMedia(sheet.media.mediaText) || dependsOnMedia(sheet.cssRules));
}

/** The parts `checkCompleteness` composes, passed in so each stays embeddable on its own. */
export type CompletenessChecks = {
	findNameRuns: typeof findNameRuns;
	isNameShown: typeof isNameShown;
	hasMediaRules: typeof hasMediaRules;
};

export const COMPLETENESS_CHECKS: CompletenessChecks = { findNameRuns, isNameShown, hasMediaRules };

export function checkCompleteness(doc: Document, shifts: CompletenessShift[], measure: CompletenessMeasure, checks: CompletenessChecks): CompletenessReport {
	const SHIFT_ID_ATTRIBUTE = "data-shift-id";
	const tagged = new Map<string, Element[]>();
	for (const element of Array.from(doc.querySelectorAll(`[${SHIFT_ID_ATTRIBUTE}]`))) {
		// The selector matched on the attribute, so it is present.
		const id = element.getAttribute(SHIFT_ID_ATTRIBUTE) as string;
		const elements = tagged.get(id);
		if (elements === undefined) tagged.set(id, [element]);
		else elements.push(element);
	}
	const showsShift = (shift: CompletenessShift): boolean => {
		const elements = tagged.get(shift.id);
		if (elements === undefined) return false;
		return elements.some((element) => checks.findNameRuns(element, shift.name).some((runs) => checks.isNameShown(element, runs, measure)));
	};
	const shiftIds = new Set(shifts.map((shift) => shift.id));
	const missing = shifts.filter((shift) => !showsShift(shift)).map((shift) => shift.id);
	const mediaRules = checks.hasMediaRules(doc);
	return {
		ok: missing.length === 0 && !mediaRules,
		missing,
		unknownIds: [...tagged.keys()].filter((id) => !shiftIds.has(id)),
		mediaRules,
	};
}

/**
 * The real browser measure, run inside the harness iframe. Self-contained like the checks. Null when the
 * browser lacks `Element.checkVisibility`, without which visibility can't be told reliably.
 */
export function createDomMeasure(win: Window): CompletenessMeasure | null {
	const PERCENT = 100;
	// Overflow doesn't apply to these boxes, whatever their computed `overflow` says.
	const NON_CLIPPING_DISPLAYS = [
		"inline",
		"contents",
		"none",
		"table-row",
		"table-row-group",
		"table-header-group",
		"table-footer-group",
		"table-column",
		"table-column-group",
	];
	// Both spellings: `opacityProperty`/`visibilityProperty` are the standard names, `checkOpacity`/`checkVisibilityCSS`
	// the ones browsers shipped first. Each browser reads the pair it knows.
	const VISIBILITY_OPTIONS = { opacityProperty: true, visibilityProperty: true, checkOpacity: true, checkVisibilityCSS: true };
	if (typeof win.document.documentElement.checkVisibility !== "function") return null;
	const alphaOf = (color: string): number => {
		if (color === "transparent") return 0;
		const inner = color.slice(color.indexOf("(") + 1, color.lastIndexOf(")"));
		const slashIndex = inner.indexOf("/");
		const commaParts = inner.split(",");
		const alphaText = slashIndex >= 0 ? inner.slice(slashIndex + 1) : commaParts.length === 4 ? commaParts[3] : null;
		// DEFAULT: CSS colours written without an alpha component are fully opaque.
		if (alphaText === null) return 1;
		const trimmed = alphaText.trim();
		return trimmed.endsWith("%") ? Number.parseFloat(trimmed) / PERCENT : Number.parseFloat(trimmed);
	};
	const boxOf = (rect: DOMRectReadOnly): CompletenessBox => ({ left: rect.left, top: rect.top, width: rect.width, height: rect.height });
	return {
		isVisible: (element) => element.checkVisibility(VISIBILITY_OPTIONS),
		styleOf: (element) => {
			const style = win.getComputedStyle(element);
			const canClip = !NON_CLIPPING_DISPLAYS.includes(style.display);
			return {
				overflowX: canClip ? style.overflowX : "visible",
				overflowY: canClip ? style.overflowY : "visible",
				fontSizePx: Number.parseFloat(style.fontSize),
				colorAlpha: alphaOf(style.color),
			};
		},
		textBoxes: (run) => {
			const range = win.document.createRange();
			range.setStart(run.node, run.start);
			range.setEnd(run.node, run.end);
			return Array.from(range.getClientRects(), boxOf);
		},
		clipBoxOf: (element) => {
			const rect = element.getBoundingClientRect();
			return { left: rect.left + element.clientLeft, top: rect.top + element.clientTop, width: element.clientWidth, height: element.clientHeight };
		},
		overflowOf: (element) => ({ x: element.scrollWidth > element.clientWidth, y: element.scrollHeight > element.clientHeight }),
		pageBox: () => boxOf(win.document.documentElement.getBoundingClientRect()),
	};
}
